import { useEffect, useState } from "react";
import axios from "axios";
import { AnimatePresence, motion } from "framer-motion";
import { AlertCircle, CheckCircle2, ShieldCheck } from "lucide-react";
import { Overline, Reveal } from "./Shared";
import { API_BASE, US_STATES } from "@/constants/form";

const EASE = [0.16, 1, 0.3, 1];

const formatPhone = (value) => {
  const d = value.replace(/\D/g, "").slice(0, 10);
  if (d.length > 6) return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
  if (d.length > 3) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
  if (d.length > 0) return `(${d}`;
  return "";
};

// Validation is config-driven: `campaign.questions` comes from MongoDB via
// GET /api/campaigns. No campaign-specific logic lives in this component.
const validate = (f, answers, campaign) => {
  const errors = {};
  if (!f.campaign) errors.campaign = "Please select the type of case.";
  if (!f.first_name.trim()) errors.first_name = "First name is required.";
  if (!f.last_name.trim()) errors.last_name = "Last name is required.";
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.email.trim()))
    errors.email = "Enter a valid email address.";
  const digits = f.phone.replace(/\D/g, "");
  if (!(digits.length === 10 || (digits.length === 11 && digits.startsWith("1"))))
    errors.phone = "Enter a valid 10-digit U.S. phone number.";
  if (!f.state) errors.state = "Select your state.";
  if (!/^\d{5}(-\d{4})?$/.test(f.zip.trim()))
    errors.zip = "Enter a valid 5-digit ZIP code.";
  (campaign?.questions || []).forEach((q) => {
    const v = answers[q.id];
    const empty =
      v === undefined || v === "" || (Array.isArray(v) && v.length === 0);
    if (q.required && empty) errors[`q_${q.id}`] = "This question is required.";
  });
  if (campaign && campaign.consent_required !== false && !f.consent)
    errors.consent = "Please review and accept the disclosure to continue.";
  return errors;
};

const inputCls = (hasError) =>
  `mt-2 w-full bg-[#FFFFFF] border rounded-sm px-4 py-3.5 font-sans text-base text-[#1A1715] placeholder:text-[#A8A29E] focus:outline-none transition-colors ${
    hasError
      ? "border-[#B91C1C] focus:border-[#B91C1C] focus:ring-1 focus:ring-[#B91C1C]"
      : "border-[#E7E2DA] focus:border-[#C2410C] focus:ring-1 focus:ring-[#C2410C]"
  }`;

const labelCls =
  "block font-mono text-[11px] uppercase tracking-[0.18em] text-[#44403C] font-semibold";

const FieldError = ({ id, message }) =>
  message ? (
    <p
      id={id}
      data-testid={`field-error-${id.replace("-error", "")}`}
      className="mt-1.5 font-sans text-xs font-medium text-[#B91C1C]"
      role="alert"
    >
      {message}
    </p>
  ) : null;

// Renders one admin-configured qualification question. Supported types:
// text, select, radio, checkbox (multi), date, number.
const QuestionField = ({ q, value, onChange, error }) => {
  const errId = `q-${q.id}-error`;
  const inputId = `q-${q.id}`;
  return (
    <div data-testid={`question-field-${q.id}`}>
      <label htmlFor={inputId} className={labelCls}>
        {q.label}{" "}
        {q.required ? (
          <span className="text-[#C2410C]">*</span>
        ) : (
          <span className="normal-case text-[#A8A29E]">(optional)</span>
        )}
      </label>
      {q.type === "text" && (
        <input
          id={inputId}
          data-testid={`question-input-${q.id}`}
          type="text"
          className={inputCls(!!error)}
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          aria-invalid={!!error}
          aria-describedby={error ? errId : undefined}
        />
      )}
      {q.type === "number" && (
        <input
          id={inputId}
          data-testid={`question-input-${q.id}`}
          type="number"
          inputMode="decimal"
          className={inputCls(!!error)}
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
          aria-invalid={!!error}
          aria-describedby={error ? errId : undefined}
        />
      )}
      {q.type === "date" && (
        <input
          id={inputId}
          data-testid={`question-input-${q.id}`}
          type="date"
          className={inputCls(!!error)}
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          aria-invalid={!!error}
          aria-describedby={error ? errId : undefined}
        />
      )}
      {q.type === "select" && (
        <select
          id={inputId}
          data-testid={`question-input-${q.id}`}
          className={`${inputCls(!!error)} ${!value ? "text-[#A8A29E]" : ""}`}
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          aria-invalid={!!error}
          aria-describedby={error ? errId : undefined}
        >
          <option value="" disabled>
            Select…
          </option>
          {(q.options || []).map((o) => (
            <option key={o} value={o}>
              {o}
            </option>
          ))}
        </select>
      )}
      {q.type === "radio" && (
        <div className="mt-3 space-y-2.5" role="radiogroup" aria-label={q.label}>
          {(q.options || []).map((o, i) => (
            <label key={o} className="flex items-center gap-3 cursor-pointer">
              <input
                type="radio"
                name={inputId}
                data-testid={`question-input-${q.id}-option-${i}`}
                checked={value === o}
                onChange={() => onChange(o)}
                className="h-4 w-4 accent-[#C2410C]"
              />
              <span className="font-sans text-sm text-[#1A1715]">{o}</span>
            </label>
          ))}
        </div>
      )}
      {q.type === "checkbox" && (
        <div className="mt-3 space-y-2.5">
          {(q.options || []).map((o, i) => {
            const arr = Array.isArray(value) ? value : [];
            const checked = arr.includes(o);
            return (
              <label key={o} className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  data-testid={`question-input-${q.id}-option-${i}`}
                  checked={checked}
                  onChange={() =>
                    onChange(checked ? arr.filter((v) => v !== o) : [...arr, o])
                  }
                  className="h-4 w-4 rounded-sm accent-[#C2410C]"
                />
                <span className="font-sans text-sm text-[#1A1715]">{o}</span>
              </label>
            );
          })}
        </div>
      )}
      <FieldError id={errId} message={error} />
    </div>
  );
};

const LeadForm = ({ campaigns, selected, onSelect }) => {
  const [form, setForm] = useState({
    campaign: "",
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    state: "",
    zip: "",
    brief_notes: "",
    consent: false,
  });
  const [errors, setErrors] = useState({});
  const [answers, setAnswers] = useState({});
  const [status, setStatus] = useState("idle"); // idle | submitting | success
  const [serverError, setServerError] = useState("");
  const [leadId, setLeadId] = useState("");

  useEffect(() => {
    if (selected) setForm((f) => ({ ...f, campaign: selected }));
  }, [selected]);

  const selectedCampaign = campaigns.find((c) => c.id === form.campaign);

  const set = (key) => (e) => {
    const value = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((f) => ({
      ...f,
      [key]: key === "phone" ? formatPhone(value) : value,
    }));
    if (errors[key]) setErrors((er) => ({ ...er, [key]: undefined }));
    if (key === "campaign") setAnswers({});
    if (key === "campaign" && onSelect) onSelect(value, { silent: true });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (status === "submitting" || status === "success") return;
    setServerError("");

    const errs = validate(form, answers, selectedCampaign);
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }
    setErrors({});
    setStatus("submitting");

    // Coerce dynamic answers to their configured types before submission.
    const qualification_data = {};
    (selectedCampaign?.questions || []).forEach((q) => {
      const v = answers[q.id];
      if (v === undefined || v === "" || (Array.isArray(v) && v.length === 0))
        return;
      qualification_data[q.id] = q.type === "number" ? Number(v) : v;
    });

    // TrustedForm injects and populates this hidden field on the page.
    const tfField = document.querySelector('input[name="xxTrustedFormCertUrl"]');
    const trustedform_cert_url = tfField && tfField.value ? tfField.value : null;

    try {
      const { data } = await axios.post(`${API_BASE}/leads`, {
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        email: form.email.trim(),
        phone: form.phone,
        state: form.state,
        zip: form.zip.trim(),
        campaign: form.campaign,
        brief_notes: form.brief_notes.trim() || null,
        consent: {
          consent_given: form.consent,
          consent_version: selectedCampaign?.consent_version || null,
        },
        qualification_data,
        trustedform_cert_url,
        jornaya_lead_id: null,
        source: "landing_page",
        landing_page: "lawsuitsettlementhelp.com",
      });
      setLeadId(data.lead_id);
      setStatus("success");
    } catch (err) {
      const statusCode = err.response?.status;
      if (statusCode === 409) {
        setServerError(
          err.response?.data?.detail ||
            "We have already received a recent inquiry with these details."
        );
      } else if (statusCode === 422) {
        setServerError(
          "Some details look incomplete. Please review the form and try again."
        );
      } else {
        setServerError(
          "We could not process your submission right now. Please try again in a moment."
        );
      }
      setStatus("idle");
    }
  };

  if (status === "success") {
    return (
      <section
        id="case-review"
        data-testid="form-success-section"
        className="border-b border-[#E7E2DA] bg-[#F4EFEA] py-20 sm:py-28"
      >
        <div className="mx-auto max-w-3xl px-5 sm:px-10">
          <motion.div
            data-testid="form-success-panel"
            className="bg-[#FFFFFF] border border-[#BBF7D0] rounded-sm p-8 sm:p-12 shadow-[0_12px_40px_rgba(26,23,21,0.06)]"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: EASE }}
          >
            <div className="flex items-center gap-3">
              <CheckCircle2 className="h-7 w-7 text-[#166534]" strokeWidth={1.75} />
              <Overline className="!text-[#166534]">Submission Confirmed</Overline>
            </div>
            <h2 className="mt-5 font-serif font-semibold text-3xl sm:text-4xl tracking-tight text-[#1A1715]">
              Your inquiry has been received and logged.
            </h2>
            <p className="mt-4 font-news text-base text-[#44403C] leading-relaxed">
              Thank you, {form.first_name}. Your information has been securely
              recorded. If your inquiry matches an active review, a legal intake
              coordinator may contact you using the details you provided.
              Nothing else is required from you right now.
            </p>
            <div className="mt-8 border border-[#E7E2DA] rounded-sm bg-[#FBF9F5] px-5 py-4 flex flex-wrap items-center justify-between gap-3">
              <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#78716C]">
                Reference
              </span>
              <span
                data-testid="thank-you-reference"
                className="font-mono text-sm font-semibold text-[#1A1715] tracking-wider"
              >
                {leadId}
              </span>
            </div>
            <ul className="mt-8 space-y-3">
              {[
                "Confidential and no obligation — you decide what happens next.",
                "Keep any relevant records (medical, purchase, or incident) nearby in case they are requested.",
                "Submitting this form does not create an attorney-client relationship.",
              ].map((item) => (
                <li key={item} className="flex items-start gap-3">
                  <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-[#B45309]" strokeWidth={2} />
                  <span className="font-sans text-sm text-[#44403C]">{item}</span>
                </li>
              ))}
            </ul>
          </motion.div>
        </div>
      </section>
    );
  }

  return (
    <section
      id="case-review"
      data-testid="lead-form-section"
      className="border-b border-[#E7E2DA] bg-[#F4EFEA] py-20 sm:py-28 scroll-mt-20"
    >
      <div className="mx-auto max-w-7xl px-5 sm:px-10 lg:px-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <Reveal>
              <Overline>Chapter 03 — Free Case Review</Overline>
              <h2 className="mt-4 font-serif font-semibold text-3xl sm:text-4xl tracking-tight text-[#1A1715] leading-tight">
                Tell us what happened. We'll take it from there.
              </h2>
              <p className="mt-5 font-news text-base text-[#44403C] leading-relaxed">
                This review is free and confidential. There is no obligation,
                and submitting does not create an attorney-client relationship.
                Fields marked <span className="text-[#C2410C]">*</span> are
                required.
              </p>
              <div className="mt-10 overflow-hidden rounded-sm border border-[#E7E2DA]">
                <img
                  src="https://images.pexels.com/photos/48148/document-agreement-documents-sign-48148.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                  alt="A fountain pen signing a document, representing individual case review"
                  data-testid="form-context-image"
                  className="h-52 w-full object-cover"
                  loading="lazy"
                />
              </div>
              <p className="mt-4 font-serif italic text-sm text-[#78716C]">
                Every inquiry is reviewed individually — never auto-decided.
              </p>
            </Reveal>
          </div>

          <div className="lg:col-span-7">
            <Reveal delay={0.1}>
              <form
                data-testid="lead-form"
                onSubmit={handleSubmit}
                noValidate
                className="bg-[#FFFFFF] border border-[#E7E2DA] rounded-sm p-6 sm:p-10 shadow-[0_12px_40px_rgba(26,23,21,0.06)]"
              >
                <AnimatePresence>
                  {serverError && (
                    <motion.div
                      data-testid="form-error-banner"
                      className="mb-8 flex items-start gap-3 border border-[#FECACA] bg-[#FEF2F2] rounded-sm px-4 py-3.5"
                      initial={{ opacity: 0, y: -8 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0 }}
                      role="alert"
                    >
                      <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-[#B91C1C]" />
                      <p className="font-sans text-sm font-medium text-[#B91C1C]">
                        {serverError}
                      </p>
                    </motion.div>
                  )}
                </AnimatePresence>

                <div>
                  <label htmlFor="campaign" className={labelCls}>
                    Type of Case <span className="text-[#C2410C]">*</span>
                  </label>
                  <select
                    id="campaign"
                    data-testid="campaign-select"
                    value={form.campaign}
                    onChange={set("campaign")}
                    aria-invalid={!!errors.campaign}
                    aria-describedby={errors.campaign ? "campaign-error" : undefined}
                    className={`${inputCls(!!errors.campaign)} ${!form.campaign ? "text-[#A8A29E]" : ""}`}
                  >
                    <option value="" disabled>
                      Select your case type…
                    </option>
                    {campaigns.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                  <FieldError id="campaign-error" message={errors.campaign} />
                </div>

                <div className="mt-7 grid grid-cols-1 sm:grid-cols-2 gap-7">
                  <div>
                    <label htmlFor="first_name" className={labelCls}>
                      First Name <span className="text-[#C2410C]">*</span>
                    </label>
                    <input
                      id="first_name"
                      data-testid="first-name-input"
                      type="text"
                      autoComplete="given-name"
                      value={form.first_name}
                      onChange={set("first_name")}
                      aria-invalid={!!errors.first_name}
                      aria-describedby={errors.first_name ? "first-name-error" : undefined}
                      className={inputCls(!!errors.first_name)}
                      placeholder="Jane"
                    />
                    <FieldError id="first-name-error" message={errors.first_name} />
                  </div>
                  <div>
                    <label htmlFor="last_name" className={labelCls}>
                      Last Name <span className="text-[#C2410C]">*</span>
                    </label>
                    <input
                      id="last_name"
                      data-testid="last-name-input"
                      type="text"
                      autoComplete="family-name"
                      value={form.last_name}
                      onChange={set("last_name")}
                      aria-invalid={!!errors.last_name}
                      aria-describedby={errors.last_name ? "last-name-error" : undefined}
                      className={inputCls(!!errors.last_name)}
                      placeholder="Doe"
                    />
                    <FieldError id="last-name-error" message={errors.last_name} />
                  </div>
                </div>

                <div className="mt-7 grid grid-cols-1 sm:grid-cols-2 gap-7">
                  <div>
                    <label htmlFor="email" className={labelCls}>
                      Email Address <span className="text-[#C2410C]">*</span>
                    </label>
                    <input
                      id="email"
                      data-testid="email-input"
                      type="email"
                      autoComplete="email"
                      inputMode="email"
                      value={form.email}
                      onChange={set("email")}
                      aria-invalid={!!errors.email}
                      aria-describedby={errors.email ? "email-error" : undefined}
                      className={inputCls(!!errors.email)}
                      placeholder="jane.doe@example.com"
                    />
                    <FieldError id="email-error" message={errors.email} />
                  </div>
                  <div>
                    <label htmlFor="phone" className={labelCls}>
                      Phone Number <span className="text-[#C2410C]">*</span>
                    </label>
                    <input
                      id="phone"
                      data-testid="phone-input"
                      type="tel"
                      autoComplete="tel-national"
                      inputMode="tel"
                      value={form.phone}
                      onChange={set("phone")}
                      aria-invalid={!!errors.phone}
                      aria-describedby={errors.phone ? "phone-error" : undefined}
                      className={inputCls(!!errors.phone)}
                      placeholder="(555) 123-4567"
                    />
                    <FieldError id="phone-error" message={errors.phone} />
                  </div>
                </div>

                <div className="mt-7 grid grid-cols-1 sm:grid-cols-2 gap-7">
                  <div>
                    <label htmlFor="state" className={labelCls}>
                      State <span className="text-[#C2410C]">*</span>
                    </label>
                    <select
                      id="state"
                      data-testid="state-select"
                      value={form.state}
                      onChange={set("state")}
                      aria-invalid={!!errors.state}
                      aria-describedby={errors.state ? "state-error" : undefined}
                      className={`${inputCls(!!errors.state)} ${!form.state ? "text-[#A8A29E]" : ""}`}
                    >
                      <option value="" disabled>
                        Select state…
                      </option>
                      {US_STATES.map((s) => (
                        <option key={s} value={s}>
                          {s}
                        </option>
                      ))}
                    </select>
                    <FieldError id="state-error" message={errors.state} />
                  </div>
                  <div>
                    <label htmlFor="zip" className={labelCls}>
                      ZIP Code <span className="text-[#C2410C]">*</span>
                    </label>
                    <input
                      id="zip"
                      data-testid="zip-input"
                      type="text"
                      inputMode="numeric"
                      autoComplete="postal-code"
                      maxLength={10}
                      value={form.zip}
                      onChange={set("zip")}
                      aria-invalid={!!errors.zip}
                      aria-describedby={errors.zip ? "zip-error" : undefined}
                      className={inputCls(!!errors.zip)}
                      placeholder="90210"
                    />
                    <FieldError id="zip-error" message={errors.zip} />
                  </div>
                </div>

                <div className="mt-7">
                  <label htmlFor="brief_notes" className={labelCls}>
                    Brief Summary <span className="normal-case text-[#A8A29E]">(optional)</span>
                  </label>
                  <textarea
                    id="brief_notes"
                    data-testid="notes-input"
                    rows={3}
                    value={form.brief_notes}
                    onChange={set("brief_notes")}
                    className={`${inputCls(false)} resize-y`}
                    placeholder="Anything that helps us understand your situation — dates, diagnosis, exposure."
                  />
                </div>

                {selectedCampaign?.questions?.length > 0 && (
                  <div
                    data-testid="dynamic-questions"
                    className="mt-9 space-y-7 border-t border-[#E7E2DA] pt-8"
                  >
                    <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#78716C]">
                      A few questions about your situation
                    </p>
                    {selectedCampaign.questions.map((q) => (
                      <QuestionField
                        key={q.id}
                        q={q}
                        value={answers[q.id]}
                        error={errors[`q_${q.id}`]}
                        onChange={(v) => {
                          setAnswers((a) => ({ ...a, [q.id]: v }));
                          if (errors[`q_${q.id}`])
                            setErrors((er) => ({ ...er, [`q_${q.id}`]: undefined }));
                        }}
                      />
                    ))}
                  </div>
                )}

                {selectedCampaign && selectedCampaign.consent_required !== false && (
                  <div
                    data-testid="consent-disclosure"
                    className="mt-9 border border-[#E7E2DA] bg-[#FBF9F5] rounded-sm p-5"
                  >
                    <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#78716C] mb-3">
                      Consent & Disclosure
                    </p>
                    <p
                      data-testid="consent-text"
                      className="font-sans text-xs leading-relaxed text-[#44403C]"
                    >
                      {selectedCampaign.consent_text}{" "}
                    See our{" "}
                    <a
                      href="/privacy-policy"
                      data-testid="consent-privacy-link"
                      className="underline decoration-[#C2410C] underline-offset-2 hover:text-[#C2410C] transition-colors"
                    >
                      Privacy Policy
                    </a>{" "}
                    and{" "}
                    <a
                      href="/terms-of-use"
                      data-testid="consent-terms-link"
                      className="underline decoration-[#C2410C] underline-offset-2 hover:text-[#C2410C] transition-colors"
                    >
                      Terms of Use
                    </a>
                    .
                  </p>
                  <label
                    htmlFor="consent"
                    className="mt-4 flex items-start gap-3 cursor-pointer"
                  >
                    <input
                      id="consent"
                      data-testid="consent-checkbox"
                      type="checkbox"
                      checked={form.consent}
                      onChange={set("consent")}
                      aria-invalid={!!errors.consent}
                      aria-describedby={errors.consent ? "consent-error" : undefined}
                      className="mt-0.5 h-5 w-5 shrink-0 rounded-sm border-[#A8A29E] accent-[#C2410C] cursor-pointer"
                    />
                    <span className="font-sans text-sm font-medium text-[#1A1715]">
                      I have read and agree to the disclosure above.{" "}
                      <span className="text-[#C2410C]">*</span>
                    </span>
                  </label>
                  <FieldError id="consent-error" message={errors.consent} />
                </div>
                )}

                <button
                  type="submit"
                  data-testid="submit-lead-button"
                  disabled={status === "submitting"}
                  className="mt-9 w-full inline-flex items-center justify-center gap-3 bg-[#C2410C] text-[#FBF9F5] font-sans font-semibold text-base px-8 py-4 rounded-sm transition-colors duration-300 hover:bg-[#9A3412] disabled:opacity-60 disabled:cursor-not-allowed focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[#C2410C]"
                >
                  {status === "submitting" ? (
                    <>
                      <span className="submit-spinner h-4 w-4 rounded-full border-2 border-[#FBF9F5]/40 border-t-[#FBF9F5]" />
                      Submitting Securely…
                    </>
                  ) : (
                    "Request My Free Case Review"
                  )}
                </button>
                <p
                  data-testid="recording-notice"
                  className="mt-4 text-center font-sans text-xs leading-relaxed text-[#78716C]"
                >
                  Secure submission. Your interactions on this page may be
                  recorded by TrustedForm to document consent — see our{" "}
                  <a
                    href="/privacy-policy"
                    data-testid="recording-notice-privacy-link"
                    className="underline underline-offset-2 hover:text-[#C2410C] transition-colors"
                  >
                    Privacy Policy
                  </a>
                  .
                </p>
              </form>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
};

export default LeadForm;
